"use client";

import { Loader2, MapPin } from "lucide-react";
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";

import { searchPlaces } from "../../_logic/place-catalog";
import { isConfident, type PlaceOption } from "../../_logic/places";

/** Imperative handle, so a list can focus a row's picker. */
export interface PlaceComboboxHandle {
  focus: () => void;
}

/** How a picker behaves in its list: quick picks, kept text, paste and keyboard handlers. Passed through as is. */
export interface PlaceComboboxBehavior {
  /** Places offered when the field is empty, e.g. the org's frequent home cities. */
  quickPicks?: { heading: string; options: PlaceOption[] };
  /** Unpicked text from an earlier visit; it comes back flagged "Pick a city from the list". */
  draftText?: string;
  /** Reports typed text, so it can outlive the popover. */
  onDraftText?: (text: string) => void;
  /** Handles a pasted list; return true when it was one, so the paste isn't typed in. */
  onPasteList?: (text: string) => boolean;
  /** Enter on an empty, closed picker, e.g. to add another row. */
  onEnterEmpty?: () => void;
  /** Backspace on an empty picker, e.g. to drop an empty row. */
  onBackspaceEmpty?: () => void;
  autoFocus?: boolean;
}

interface PlaceComboboxProps {
  /** The picked city, or null for an empty row. */
  value: string | null;
  placeholder: string;
  ariaLabel: string;
  onPick: (place: PlaceOption) => void;
  behavior?: PlaceComboboxBehavior;
}

/**
 * A city picker that only accepts real places. Search understands cities, airport names and
 * codes, aliases ("nyc", "sf"), states and countries ("texas" lists Texas's cities) and typos.
 * The first result is highlighted; Enter takes it only when it is a confident match (or the
 * planner moved to it), so a typo or a state never becomes a city by accident. Text that wasn't
 * picked shows "Pick a city from the list". Password managers and autofill are kept off.
 * @param props - The picked city, labels, quick picks, and the pick, paste and keyboard handlers.
 * @returns The picker.
 * @throws Never.
 */
export const PlaceCombobox = forwardRef<PlaceComboboxHandle, PlaceComboboxProps>(function PlaceCombobox(
  { value, placeholder, ariaLabel, onPick, behavior = {} },
  ref
) {
  const {
    quickPicks,
    draftText,
    onDraftText,
    onPasteList,
    onEnterEmpty,
    onBackspaceEmpty,
    autoFocus = false,
  } = behavior;

  // Hooks
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({ focus: () => input.current?.focus() }), []);

  // States
  const [text, setText] = useState(value ?? draftText ?? "");
  const [results, setResults] = useState<{ heading: string | null; options: PlaceOption[] }>({
    heading: null,
    options: [],
  });
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    !value && draftText?.trim() ? "Pick a city from the list" : null
  );
  const query = useRef(0);
  /** The planner moved through the list, so Enter takes what they're on. */
  const navigated = useRef(false);

  // Derived
  const typed = text.trim();
  const searching = Boolean(typed) && typed !== value;
  const showingQuickPicks = !typed && Boolean(quickPicks?.options.length);
  const list = searching ? results : showingQuickPicks ? quickPicks! : { heading: null, options: [] };
  const options = list.options;

  // Handlers
  const pick = (option: PlaceOption) => {
    setText(option.city);
    setOpen(false);
    setError(null);
    onPick(option);
  };

  const move = (step: number) => {
    if (!options.length) return;
    navigated.current = true;
    setOpen(true);
    setHighlighted((index) => (index + step + options.length) % options.length);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = open ? options[highlighted] : undefined;
      if (option && (navigated.current || (searching && isConfident(option)))) pick(option);
      else if (!typed || typed === value) onEnterEmpty?.();
      else {
        // Close the list so the message shows; ArrowDown reopens it to choose.
        setOpen(false);
        setError("Pick a city from the list");
      }
    } else if (event.key === "Escape" && open) {
      event.stopPropagation();
      setOpen(false);
      setText(value ?? "");
    } else if (event.key === "Backspace" && !text) {
      onBackspaceEmpty?.();
    }
  };

  const onBlur = (event: React.FocusEvent) => {
    if (wrapper.current?.contains(event.relatedTarget as Node | null)) return;
    setOpen(false);
    if (!typed) {
      setText(value ?? "");
      setError(null);
    } else if (typed !== value) {
      setError("Pick a city from the list");
    }
  };

  // Effects
  useEffect(() => {
    if (autoFocus) input.current?.focus();
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // A picked value replaces the text; an empty row keeps what was typed.
    if (value !== null) setText(value);
  }, [value]);

  useEffect(() => {
    navigated.current = false;
    setHighlighted(0);
    if (!searching) return;
    // Searched in the browser, so there is nothing to debounce: results follow every keystroke.
    const id = ++query.current;
    setLoading(true);
    void searchPlaces(typed).then((found) => {
      if (id !== query.current) return;
      setResults(found);
      setLoading(false);
    });
  }, [typed, searching]);

  // Render
  return (
    <div ref={wrapper} className="relative flex min-w-0 flex-1 flex-col gap-1" onBlur={onBlur}>
      <div
        className={`flex h-10 items-center gap-2 rounded-xl border bg-white px-3 transition-colors focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 ${error ? "border-red-300" : "border-gray-200 hover:border-gray-300"}`}
      >
        <MapPin aria-hidden className="size-4 shrink-0 text-gray-400" />
        <input
          ref={input}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open && options.length > 0}
          aria-controls={listId}
          aria-activedescendant={open && options.length > 0 ? `${listId}-${highlighted}` : undefined}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          name="estimator-place-search"
          autoComplete="off"
          data-1p-ignore=""
          data-lpignore="true"
          data-form-type="other"
          spellCheck={false}
          value={text}
          placeholder={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            onDraftText?.(event.target.value);
            setError(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text");
            if (onPasteList && /[\n,;\t]|\d/.test(pasted) && onPasteList(pasted)) event.preventDefault();
          }}
          className="min-w-0 flex-1 bg-transparent text-base font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none md:text-sm"
        />
        {loading && searching && <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-gray-400" />}
      </div>
      {error && (
        <p role="alert" className="px-1 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
      {open && (showingQuickPicks || (searching && !loading)) && (
        <div className="absolute inset-x-0 top-11 z-30 flex max-h-72 flex-col overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
          {list.heading && <p className="px-3 pb-1 pt-2 text-xs font-semibold text-gray-600">{list.heading}</p>}
          <ul id={listId} role="listbox" aria-label={list.heading ?? "Places"} className="flex list-none flex-col">
            {options.length === 0 ? (
              <li
                role="option"
                aria-disabled="true"
                aria-selected={false}
                className="px-3 py-2 text-sm font-medium text-gray-600"
              >
                No city matches “{typed}”
              </li>
            ) : (
              options.map((option, index) => (
                // The option itself takes the click; focus stays in the field, which points at it.
                <li
                  key={option.codes[0] ?? `${option.city}-${option.context}`}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === highlighted}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => pick(option)}
                  className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium ${index === highlighted ? "bg-gray-100 text-gray-900" : "text-gray-700"}`}
                >
                  <span className="truncate" title={`${option.city}, ${option.context}`}>
                    {option.city}
                    <span className="text-gray-600">, {option.context}</span>
                  </span>
                  <span className="shrink-0 text-xs font-medium tabular-nums text-gray-600">
                    {option.codes.slice(0, 3).join("/")}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
});
